<template>
  <section class='section'>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <h1>Productos: {{total}}</h1>
        </div>
      </div>
      <div class="toolbar">
        <div class="control has-icons-left">
          <input class="input" v-model="search" @input="searchProducts" placeholder="Buscar por producto...">
          <span class="icon is-small is-left">
            <i class="material-icons toolbar-icon">search</i>
          </span>
        </div>
        <div class="control has-icons-left">
          <input class="input" v-model="currentFilters.username" @input="onUsernameInput" placeholder="Buscar por usuario...">
          <span class="icon is-small is-left">
            <i class="material-icons toolbar-icon">person</i>
          </span>
        </div>
        <div class="toolbar-dates">
          <button @click="toggleDatePicker" v-if="!showDatePicker" class="button">
            Filtrar por fecha
          </button>
          <div v-else style="max-width: 150px; overflow: hidden;">
            <v-date-picker
              mode="range"
              v-model="date"
              :max-date="today"
              @input="onDateChange"
            />
          </div>
          <button @click="resetDate" v-if="showDatePicker" class="button" style="padding: 4px;">
            <span class="material-icons">
              restart_alt
            </span>
          </button>
        </div>
        <button class="button" type="button" @click="openFiltersModal">
          <span class="material-icons toolbar-icon">tune</span>
          <span class="ml-1">Filtros</span>
          <span class="filters-badge" v-if="activeFiltersCount">{{ activeFiltersCount }}</span>
        </button>
        <button
          class="button"
          type="button"
          :class="{ 'is-loading': isLoading }"
          @click="getCsvDownload"
        >
          Descargar
        </button>
      </div>
    </nav>
    <div class="filter-chips" v-if="activeFilterChips.length">
      <span class="filter-chip" v-for="chip in activeFilterChips" :key="chip.key">
        <span class="filter-chip-label">{{ chip.label }}:</span>
        <span>{{ chip.value }}</span>
        <button class="filter-chip-close" type="button" @click="removeChip(chip)" aria-label="Quitar filtro">×</button>
      </span>
      <button class="button is-small is-text" type="button" @click="clearAllFilters">Limpiar todo</button>
    </div>
    <div class="columns is-multiline">
      <div class="column is-12">
        <base-table
          :columns="computedColumnsData"
          :data="productList"
          :loading=loading
          @sort="handleSort"
        >
        <div
          slot="removeColumn"
          slot-scope="scope"
        >
          <i class="material-icons icon-action" @click="openModalRemove(scope.row)">delete_forever</i>
        </div>
        <div
          slot="funnelTouchesColumn"
          slot-scope="scope"
        >
          <div class="select is-small funnel-select">
            <select
              :value="scope.row.funnelTouches || ''"
              :disabled="savingFunnelId === scope.row.productId"
              @change="onFunnelFieldChange(scope.row, 'number_of_touches', $event.target.value)"
            >
              <option value="">Sin toques</option>
              <option
                v-for="option in touchOptions"
                :key="option.slug"
                :value="option.slug"
              >{{ option.nombre }}</option>
            </select>
          </div>
        </div>
        <div
          slot="funnelStatusColumn"
          slot-scope="scope"
        >
          <div class="select is-small funnel-select">
            <select
              :value="scope.row.funnelStatus || ''"
              :disabled="savingFunnelId === scope.row.productId"
              @change="onFunnelFieldChange(scope.row, 'overall_status', $event.target.value)"
            >
              <option value="">{{ scope.row.status }}</option>
              <option
                v-for="option in overallStatusOptions"
                :key="option.slug"
                :value="option.slug"
              >{{ option.nombre }}</option>
            </select>
          </div>
        </div>
        <div
          slot="funnelRejectionColumn"
          slot-scope="scope"
        >
          <div class="select is-small funnel-select">
            <select
              :value="scope.row.funnelRejectionReason || ''"
              :disabled="savingFunnelId === scope.row.productId"
              @change="onFunnelFieldChange(scope.row, 'rejection_reason', $event.target.value)"
            >
              <option value="">Sin motivo</option>
              <option
                v-for="option in rejectionReasonOptions"
                :key="option.slug"
                :value="option.slug"
              >{{ option.nombre }}</option>
            </select>
          </div>
          <p class="rejected-message" v-if="scope.row.rejected_message">{{ scope.row.rejected_message }}</p>
        </div>
        </base-table>
      </div>
    </div>
    <base-pagination
      class="is-medium"
      :page="currentFilters.page"
      :pages="parseInt(pages)"
      :action="paginator"
      :loading="loading"
    />
    <modal-remove
      v-if="isShowModalRemove"
      :close="closeModalRemove"
      :action="removeProduct"
      :removeProductData="removeProductData"
    />
    <product-filters-modal
      v-if="isShowFiltersModal"
      :close="closeFiltersModal"
      :apply="applyFilters"
      :filters="currentFilters"
    />
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import mixin from '../../mixin/validateDate'
import {
  TOUCH_OPTIONS,
  OVERALL_STATUS_OPTIONS,
  REJECTION_REASON_OPTIONS,
  labelBySlug
} from '@/lib/funnelOptions'

const PRODUCT_STATUS_LABELS = {
  1: 'Pendiente',
  2: 'Activo',
  3: 'Vendido',
  4: 'Eliminado'
}

const TRADE_IN_LABELS = {
  normales: 'Solo Normales',
  tradeIn: 'Solo Trade-In'
}
// const PREOWNED = 2
const ADMIN_PARRA_ID = 33

export default {
  mixins: [mixin],
  name: 'Productos',
  components: {
    BaseTable: () => import('@/components/Table'),
    BasePagination: () => import('@/components/BasePagination'),
    ModalRemove: () => import('@/components/Products/ModalRemove'),
    ProductFiltersModal: () => import('@/components/Products/ProductFiltersModal')
  },
  data () {
    return {
      addUserModal: false,
      modalEditAdmin: false,
      pages: '',
      total: 0,
      search: '',
      loading: false,
      isLoading: false,
      productList: [],
      date: {},
      today: moment().add(1, 'days').format('YYYY-MM-DD'),
      showDatePicker: false,
      showAdditionalColumn: false,
      isShowModalRemove: false,
      isShowFiltersModal: false,
      removeProductData: null,
      savingFunnelId: null,
      touchOptions: TOUCH_OPTIONS,
      overallStatusOptions: OVERALL_STATUS_OPTIONS,
      rejectionReasonOptions: REJECTION_REASON_OPTIONS,
      columnsData: [
        {
          id: 'productId', header: '#', class: 'column-mobile'
        },
        {
          id: 'removeColumn',
          name: 'removeColumn',
          header: '',
          class: 'column-mobile',
          show: this.showAdditionalColumn
        },
        { id: 'name',
          header: 'Nombre del Producto',
          class: 'column-mobile',
          type: 'link',
          link: true,
          html: true,
          accessor: (row) => `<div style="display:flex; flex-direction: column; font-weight: ${!row.openedadmin ? '800' : '200'};">
            ${row.is_trade_in ? '<div><span style="font-size: 10px; font-weight: 700; background: #ebf5ff; color: #2878c8; border: 1px solid #b3d4f5; border-radius: 4px; padding: 0px 5px; margin-bottom: 2px; display: inline-block; white-space: nowrap; text-transform: uppercase;">Trade-In</span></div>' : ''}
            <div style="display:flex; align-items: center;">
              <!-- Grid de iconos 2x2 -->
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-right: 10px; min-width: 40px; align-items: center; justify-items: center;">
                <div
                  data-tooltip="${row.statusId ? row.status : ''}"
                  class="has-tooltip-top"
                  style="width: 8px; height: 8px; border-radius: 50%; background-color: ${row.statusId === 4 ? 'hsl(348, 100%, 61%)' : ''}${row.statusId === 1 ? 'hsl(204, 86%, 53%)' : ''}${row.statusId === 2 ? 'hsl(141, 71%, 48%)' : ''}${row.statusId === 5 ? '#FFC107' : ''}"
                >
                </div>
                  
                <div
                  data-tooltip="${row.visible ? 'El producto está visible' : 'El producto se ha ocultado'}"
                >
                  ${row.visible ? '👁️' : '<span style="text-decoration:line-through;filter: grayscale(1);">👁️</span>'}
                </div>

                <div
                  data-tooltip="${row.diagnosticDate !== null ? 'Diagnóstico realizado' : ''}"
                >
                  ${row.diagnosticDate !== null ? ` 📊 ` : ''}
                </div>

                <div
                  data-tooltip="${row.deliveryDhl ? 'Envío a domicilio' : ''}"
                >
                  ${row.deliveryDhl ? ` 🚚 ` : ''}
                </div>

                <div
                  data-tooltip="${row.saleModeId ? row.saleMode : ''}"
                  class="has-tooltip-top"
                >
                  ${row.saleModeId === 1 ? ` 📦 ` : ''}${row.saleModeId === 2 ? ` 💵 ` : ''}
                </div>
              </div>

              <!-- Nombre del producto -->
              <div style="flex: 1;">
                ${row.name}
              </div>
            </div>
          </div>`,
          route: (row) => `productos/detalle/${row.productId}`
        },

        {
          id: 'nameuser',
          header: 'Usuario',
          class: 'column-mobile',
          type: 'link',
          link: true,
          route: (row) => `usuarios/detalle/${row.userId}`
        },
        {
          id: 'price',
          header: this.priceHeader,
          class: 'column-mobile',
          sortable: true,
          clickEvent: this.toggleSortOrder,
          accessor: (row) => {
            const priceDisplay = row.price ? `${parseFloat(row.price).toLocaleString('es-MX', {
              style: 'currency',
              currency: 'MXN',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}${row.abPrice ? `\n AB:${parseFloat(row.abPrice).toLocaleString('es-MX', {
              style: 'currency',
              currency: 'MXN',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}` : ``}` : 'N/A'
            return priceDisplay
          }
        },
        {
          id: 'funnelTouches',
          name: 'funnelTouchesColumn',
          header: 'Toques',
          class: 'column-mobile'
        },
        {
          id: 'status',
          name: 'funnelStatusColumn',
          header: 'Estado',
          class: 'column-mobile'
        },
        {
          id: 'rejected_message',
          name: 'funnelRejectionColumn',
          header: 'Motivo de Rechazo',
          class: 'column-mobile has-break-word'
        },
        {
          id: 'view_by_administrator',
          header: 'Visto por el admin',
          class: 'column-mobile',
          accessor: (row) => row.adminName
        },
        {
          id: 'promotion_code',
          header: 'Código',
          class: 'column-desktop',
          accessor: (row) => row.promotionCode == null ? '' : row.promotionCode
        },
        {
          id: 'createdAt',
          header: 'Fecha y hora de creación',
          class: 'column-mobile',
          accessor: (row) => this.formatDateTimestamp(row.createdAt)
        },
        {
          id: 'updatedAt',
          header: 'Fecha y hora de actualización',
          class: 'column-mobile',
          accessor: (row) => this.formatDateTimestamp(row.updatedAt)
        }
      ],
      currentFilters: {
        page: 1,
        filter: 0,
        search: '',
        sortKey: 'price',
        sortOrder: 'asc',
        username: '',
        startDate: '',
        endDate: '',
        isSold: false,
        tradeIn: 'todos',
        zipCode: '',
        region: '',
        funnelTouches: '',
        funnelStatus: '',
        rejectionReason: ''
      }
    }
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler (query) {
        this.setFiltersFromQuery(query)
        this.fetchProducts()
      }
    }
  },
  computed: {
    priceHeader () {
      return `Precio ${this.currentFilters.sortKey === 'price' ? (this.currentFilters.sortOrder === 'asc' ? '↑' : '↓') : '↑↓'}`
    },
    computedColumnsData () {
      return this.columnsData
        .filter(column => {
          if (column.id === 'removeColumn') {
            return this.showAdditionalColumn
          }
          return true
        }).map(column => {
          if (column.id === 'price') {
            return {
              ...column,
              header: `Precio ${this.currentFilters.sortKey === 'price' ? this.sortIcon : ''}`
            }
          }
          return column
        })
    },
    sortIcon () {
      return this.currentFilters.sortOrder === 'asc' ? '↑' : '↓'
    },
    // Filtros aplicados, en formato chip. `reset` es el valor que deja el filtro "en todos".
    activeFilterChips () {
      const filters = this.currentFilters
      const chips = []

      if (filters.filter && String(filters.filter) !== '0') {
        chips.push({ key: 'filter', label: 'Estado', value: PRODUCT_STATUS_LABELS[filters.filter], reset: 0 })
      }
      if (filters.tradeIn && filters.tradeIn !== 'todos') {
        chips.push({ key: 'tradeIn', label: 'Tipo', value: TRADE_IN_LABELS[filters.tradeIn], reset: 'todos' })
      }
      if (filters.funnelTouches) {
        chips.push({
          key: 'funnelTouches',
          label: 'Toques',
          value: filters.funnelTouches === 'none' ? 'Sin toques' : labelBySlug(TOUCH_OPTIONS, filters.funnelTouches),
          reset: ''
        })
      }
      if (filters.funnelStatus) {
        chips.push({
          key: 'funnelStatus',
          label: 'Estado general',
          value: filters.funnelStatus === 'none' ? 'Sin seguimiento' : labelBySlug(OVERALL_STATUS_OPTIONS, filters.funnelStatus),
          reset: ''
        })
      }
      if (filters.rejectionReason) {
        chips.push({
          key: 'rejectionReason',
          label: 'Motivo de rechazo',
          value: filters.rejectionReason === 'none' ? 'Sin motivo' : labelBySlug(REJECTION_REASON_OPTIONS, filters.rejectionReason),
          reset: ''
        })
      }
      if (filters.zipCode) {
        chips.push({ key: 'zipCode', label: 'CP', value: filters.zipCode, reset: '' })
      }
      if (filters.region) {
        chips.push({ key: 'region', label: 'Región', value: filters.region, reset: '' })
      }
      if (filters.startDate && filters.endDate) {
        chips.push({ key: 'date', label: 'Fechas', value: `${filters.startDate} → ${filters.endDate}`, reset: '' })
      }

      return chips
    },
    // El contador del botón no incluye las fechas: esas ya se ven en la barra.
    activeFiltersCount () {
      return this.activeFilterChips.filter(chip => chip.key !== 'date').length
    },
    sortedColumnsData () {
      return this.columnsData.map(column => {
        if (column.id === 'price') {
          return {
            ...column,
            header: `Precio ${this.sortKey === 'price' ? this.sortIcon : ''}`,
            clickEvent: () => this.toggleSortOrder('price')
          }
        }
        return column
      })
    }
  },
  methods: {
    ...mapActions(['getProducts', 'csvDownloadProducts', 'addTemporalNotification', 'removeProductById', 'patchAdminFunnelTracking']),
    // Edición en línea del embudo (toques / estado general / motivo de rechazo).
    // Se manda sólo el campo tocado para no pisar el resto del seguimiento.
    async onFunnelFieldChange (row, field, rawValue) {
      const value = rawValue || null
      const rowKeyByField = {
        number_of_touches: 'funnelTouches',
        overall_status: 'funnelStatus',
        rejection_reason: 'funnelRejectionReason'
      }
      const rowKey = rowKeyByField[field]
      const previousValue = row[rowKey]

      const data = { [field]: value }
      const previousRejection = row.funnelRejectionReason
      // el motivo de rechazo sólo aplica cuando el estado es "Rechazado"
      const clearsRejection = field === 'overall_status' && value !== 'rechazado' && previousRejection
      if (clearsRejection) data.rejection_reason = null

      this.$set(row, rowKey, value)
      if (clearsRejection) this.$set(row, 'funnelRejectionReason', null)

      this.savingFunnelId = row.productId
      const success = await this.patchAdminFunnelTracking({ productId: row.productId, data })
      this.savingFunnelId = null

      if (!success) {
        this.$set(row, rowKey, previousValue)
        if (clearsRejection) this.$set(row, 'funnelRejectionReason', previousRejection)
      }
    },
    paginator (newPage) {
      this.updateFilters({ page: newPage })
    },
    fetchProducts () {
      this.loading = true
      this.getProducts(this.currentFilters)
        .then(resp => {
          this.productList = resp.payload
          this.pages = resp.totalPages
          this.loading = false
          this.total = resp.total
        })
        .catch(error => {
          this.addTemporalNotification({ type: 'danger', message: error.message })
          this.loading = false
        })
    },
    parseLocalDate (dateString) {
      if (!dateString) return null
      const [y, m, d] = dateString.split('-').map(Number)
      return new Date(y, m - 1, d)
    },
    setFiltersFromQuery (query) {
      this.currentFilters.page = parseInt(query.page, 10) || 1
      this.currentFilters.filter = parseInt(query.filter, 10) || 0
      this.currentFilters.search = query.search || ''
      this.search = this.currentFilters.search
      this.currentFilters.sortKey = query.sortKey || 'product.updatedAt'
      this.currentFilters.sortOrder = query.sortOrder || 'desc'
      this.currentFilters.startDate = query.startDate || ''
      this.currentFilters.endDate = query.endDate || ''
      this.currentFilters.isSold = query.isSold || false
      this.currentFilters.tradeIn = ['todos', 'normales', 'tradeIn'].includes(query.tradeIn) ? query.tradeIn : 'todos'
      this.currentFilters.zipCode = query.zipCode || ''
      this.currentFilters.region = query.region || ''
      this.currentFilters.funnelTouches = query.funnelTouches || ''
      this.currentFilters.funnelStatus = query.funnelStatus || ''
      this.currentFilters.rejectionReason = query.rejectionReason || ''

      this.date = {
        start: this.parseLocalDate(query.startDate),
        end: this.parseLocalDate(query.endDate)
      }

      this.showDatePicker = !!(query.startDate && query.endDate)
    },
    updateFilters (newFilters) {
      this.currentFilters = { ...this.currentFilters, ...newFilters }
      this.$router.push({ name: 'productos', query: this.currentFilters }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },
    updateRouteQuery () {
      const query = { ...this.currentFilters }
      this.$router.push({ name: 'productos', query }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },
    handleSort (sortKey) {
      const newSortOrder = this.currentFilters.sortKey === sortKey && this.currentFilters.sortOrder === 'asc' ? 'desc' : 'asc'
      this.updateFilters({ sortKey, sortOrder: newSortOrder })
    },
    toggleSortOrder () {
      const newSortOrder = this.currentFilters.sortOrder === 'asc' ? 'desc' : 'asc'
      this.updateFilters({ sortOrder: newSortOrder })
    },
    sortProductList () {
      this.productList.sort((a, b) => {
        let comparison = 0
        if (a[this.sortKey] < b[this.sortKey]) {
          comparison = -1
        } else if (a[this.sortKey] > b[this.sortKey]) {
          comparison = 1
        }
        return this.sortOrder === 'desc' ? comparison * -1 : comparison
      })
    },
    searchProducts ({ target }) {
      const search = target.value
      if (search.length >= 3 || search.length === 0) {
        this.updateFilters({ search, page: 1 })
      }
    },
    onUsernameInput () {
      clearTimeout(this._usernameTimer)
      this._usernameTimer = setTimeout(() => {
        this.updateFilters({ username: this.currentFilters.username, page: 1 })
      }, 400)
    },
    openFiltersModal () {
      this.isShowFiltersModal = true
    },
    closeFiltersModal () {
      this.isShowFiltersModal = false
    },
    applyFilters (filters) {
      this.updateFilters({ ...filters, page: 1 })
    },
    removeChip (chip) {
      if (chip.key === 'date') {
        this.resetDate()
        return
      }
      this.updateFilters({ [chip.key]: chip.reset, page: 1 })
    },
    clearAllFilters () {
      this.date = {}
      this.showDatePicker = false
      this.updateFilters({
        filter: 0,
        tradeIn: 'todos',
        funnelTouches: '',
        funnelStatus: '',
        rejectionReason: '',
        zipCode: '',
        region: '',
        startDate: '',
        endDate: '',
        page: 1
      })
    },
    getCsvDownload () {
      this.isLoading = true
      // currentFilters ya viene sincronizado con la query por el watcher: el Excel
      // sale con exactamente los mismos filtros que muestra la tabla
      this.csvDownloadProducts({ ...this.currentFilters })
        .then(resp => {
          if (resp) {
            const exportName = 'productos.xlsx'
            const blob = new Blob([resp], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', exportName)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
          } else {
            this.addTemporalNotification({ type: 'danger', message: 'Error en la descarga' })
          }
          this.isLoading = false
        })
    },
    onDateChange (newDate) {
      if (newDate == null) return null

      this.currentFilters.startDate = moment(newDate?.start).format('YYYY-MM-DD')
      this.currentFilters.endDate = moment(newDate?.end).format('YYYY-MM-DD')

      this.updateFilters({ page: 1 })
    },
    toggleDatePicker () {
      this.showDatePicker = !this.showDatePicker
    },
    resetDate () {
      this.date = {}
      this.showDatePicker = false
      this.currentFilters.startDate = ''
      this.currentFilters.endDate = ''

      this.updateFilters({ page: 1 })
    },
    closeModalRemove () {
      this.removeProductData = null
      this.isShowModalRemove = false
    },
    openModalRemove (data) {
      this.removeProductData = data
      this.isShowModalRemove = true
    },
    async removeProduct (productId) {
      const response = await this.removeProductById(productId)

      this.setFiltersFromQuery(this.$route.query)
      this.fetchProducts()

      return response
    }
  },
  beforeMount () {
    // los filtros y el primer fetch los resuelve el watcher de $route.query (immediate)
    this.showAdditionalColumn = this.$store.state.authentication.adminId === ADMIN_PARRA_ID
  }
}
</script>
<style scoped>
  .section {
    width: 100%;
    padding: 0 20px 0 20px;
  }
  .icon-action {
    cursor: pointer;
    color: #039DA1;
  }
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 0;
    justify-content: flex-end;
    align-items: center;
  }
  .toolbar .control .input {
    max-width: 190px;
  }
  .toolbar-dates {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .toolbar-icon {
    font-size: 18px;
    line-height: 1;
    color: #7a7a7a;
  }
  .filters-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    margin-left: 6px;
    padding: 0 5px;
    border-radius: 9px;
    background-color: #039DA1;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
  }
  .filter-chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding-bottom: 12px;
  }
  .filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px 3px 10px;
    border: 1px solid #b3d4f5;
    border-radius: 14px;
    background-color: #ebf5ff;
    color: #2878c8;
    font-size: 12px;
    white-space: nowrap;
  }
  .filter-chip-label {
    font-weight: 700;
  }
  .filter-chip-close {
    border: none;
    background: none;
    cursor: pointer;
    color: #2878c8;
    font-size: 16px;
    line-height: 1;
    padding: 0 2px;
  }
  .filter-chip-close:hover {
    color: #1b5c9c;
  }
  .funnel-select {
    max-width: 100%;
  }
  .funnel-select select {
    font-size: 11px;
    height: 26px;
    padding-right: 24px;
    max-width: 140px;
  }
  .rejected-message {
    font-size: 10px;
    color: #7a7a7a;
    margin-top: 4px;
    word-break: break-word;
  }
</style>

<style>
  .has-break-word {
    word-break: break-word;
  }
  .level-right {
    width: fit-content;
    flex-flow: wrap;
    row-gap: 10px;
  }

  .checkbox-custom {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .checkbox-input {
    display: none;
  }

  .checkbox-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid #b3d4f5;
    border-radius: 4px;
    background-color: #ffffff;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .checkbox-label {
    color: #363636;
    font-weight: 500;
    transition: color 0.3s ease;
  }

  .checkbox-input:checked + .checkbox-box {
    background-color: #2878c8;
    border-color: #2878c8;
    box-shadow: 0 2px 4px rgba(40, 120, 200, 0.25);
  }

  .checkbox-input:checked + .checkbox-box::after {
    content: '✓';
    color: white;
    font-size: 14px;
    font-weight: bold;
  }

  .checkbox-custom:hover .checkbox-box {
    border-color: #2878c8;
    background-color: #ebf5ff;
  }

  .checkbox-custom:hover .checkbox-label {
    color: #2878c8;
  }

  .checkbox-input:checked ~ .checkbox-label {
    color: #2878c8;
    font-weight: 600;
  }

  .checkbox-input:focus + .checkbox-box {
    outline: none;
    box-shadow: 0 0 0 3px rgba(40, 120, 200, 0.1);
  }
</style>
